<script lang="ts">
  import {
    AUTH_REQUIRED, auth, role, doSignOut, openProfileModal, pbStatus,
    plannings, currentPlanningId, currentPlanning, switchPlanning, createPlanning, canAdmin,
    projects, currentProjectId, currentProject, switchProject, createProject, updateProjectSwimLanes, updateProjectTwoShifts,
    openPhasesModal, projectPhases, showToast,
  } from "../lib/stores";
  import { avatarUrl } from "../lib/pocketbase";
  import { selectValue } from "../lib/dom";
  import { theme } from "../lib/theme";
  import type { ThemeMode } from "../lib/theme";
  import { parseP6Xml } from "../lib/p6xmlImport";
  import { get } from "svelte/store";

  $: myAvatar = $auth ? avatarUrl($auth.record) : null;

  const THEME_OPTIONS: { id: ThemeMode; label: string }[] = [
    { id: "system", label: "Auto" },
    { id: "light", label: "Light" },
    { id: "dark", label: "Dark" },
  ];

  let newCraneName = "";
  let addingCrane = false;
  let newProjectName = "";
  let addingProject = false;

  // Falls back to every crane when no project context is resolved yet (e.g. existing
  // cranes that haven't been assigned a project in PocketBase admin) — the switcher
  // should keep working exactly as it did before projects existed until that's done.
  $: craneOptions = $currentProjectId ? $plannings.filter((p) => p.project === $currentProjectId) : $plannings;

  function onProjectChange(e: Event) {
    const id = selectValue(e);
    if (id) switchProject(id);
  }

  function onPlanningChange(e: Event) {
    const id = selectValue(e);
    if (id) switchPlanning(id);
  }

  async function addProject() {
    const name = newProjectName.trim();
    if (!name) return;
    await createProject(name);
    newProjectName = "";
    addingProject = false;
  }

  let craneFileInput: HTMLInputElement | undefined;

  async function addEmptyCrane() {
    const name = newCraneName.trim();
    if (!name) return;
    await createPlanning(name);
    newCraneName = "";
    addingCrane = false;
  }

  function addCraneFromP6() {
    if (!newCraneName.trim()) { showToast("Enter a crane name first"); return; }
    craneFileInput?.click();
  }

  async function onCraneFileChange(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    const name = newCraneName.trim();
    if (!file || !name) return;
    const text = await file.text();
    const result = parseP6Xml(text, get(projectPhases)[0]?.name ?? "");
    if (!result.ok) { showToast(`Couldn't import P6 XML: ${result.error}`); return; }
    await createPlanning(name, result.tasks);
    const count = result.tasks.length;
    const summary = `Imported ${count} activit${count === 1 ? "y" : "ies"} from P6 XML into ${name}` + (result.warnings.length ? ` — ${result.warnings.join(" ")}` : "");
    showToast(summary);
    newCraneName = "";
    addingCrane = false;
  }

  function onSwimLanesToggle(e: Event) {
    const id = $currentProjectId;
    if (id) updateProjectSwimLanes(id, (e.target as HTMLInputElement).checked);
  }

  function onTwoShiftsToggle(e: Event) {
    const id = $currentProjectId;
    if (id) updateProjectTwoShifts(id, (e.target as HTMLInputElement).checked);
  }
</script>

<header class="page">
  <div class="eyebrow-row">
    <div class="eyebrow">RWG · QC / BC / RC Overhaul Phase 2 · ABB Crane Control Upgrade</div>
    <div class="pm-credit">Project management support — Count &amp; Cooper</div>
  </div>
  <h1>CCU - Lean Planning Management</h1>
  <div class="subhead">
    Editable dependency network built from the LEAN planning activities. Add or remove activities and milestones, set
    predecessors and Optimistic/Most likely/Pessimistic durations, and this page computes the critical path and runs a
    Monte Carlo simulation for the true P10/P50/P90 — live, in the browser.
  </div>

  <div class="header-row">
    {#if AUTH_REQUIRED && ($projects.length || $canAdmin)}
      <div class="crane-switch">
        <label for="projectSelect">Project</label>
        <select id="projectSelect" value={$currentProjectId ?? ""} on:change={onProjectChange}>
          {#each $projects as p (p.id)}
            <option value={p.id}>{p.name}</option>
          {/each}
        </select>
        {#if $canAdmin}
          {#if addingProject}
            <input
              type="text"
              placeholder="New project, e.g. RWG Site 2"
              bind:value={newProjectName}
              on:keydown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProject(); } else if (e.key === "Escape") { addingProject = false; newProjectName = ""; } }}
            />
            <button class="btn" type="button" disabled={!newProjectName.trim()} on:click={addProject}>Add</button>
            <button class="btn" type="button" on:click={() => { addingProject = false; newProjectName = ""; }}>Cancel</button>
          {:else}
            <button class="btn" type="button" on:click={() => addingProject = true}>+ Add project</button>
          {/if}
          <label class="board-setting">
            <input type="checkbox" checked={$currentProject?.useSwimLanes ?? true} on:change={onSwimLanesToggle} />
            Swimlanes
          </label>
          <label class="board-setting" title="Splits the Lean board into two shift columns per day, and halves every activity's computed calendar-day duration since a full day's work now happens across two shifts instead of one.">
            <input type="checkbox" checked={$currentProject?.twoShifts ?? false} on:change={onTwoShiftsToggle} />
            2 shifts/day
          </label>
          <button class="btn" type="button" disabled={!$currentProjectId} on:click={openPhasesModal}>Edit swimlanes…</button>
        {/if}
      </div>
    {/if}

    {#if AUTH_REQUIRED && $plannings.length}
      <div class="crane-switch">
        <label for="craneSelect">Crane</label>
        <select id="craneSelect" value={$currentPlanningId ?? ""} on:change={onPlanningChange}>
          {#each craneOptions as p (p.id)}
            <option value={p.id}>{p.name}</option>
          {/each}
        </select>
        {#if $canAdmin}
          {#if addingCrane}
            <input
              type="text"
              placeholder="New crane, e.g. QC103"
              bind:value={newCraneName}
              on:keydown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmptyCrane(); } else if (e.key === "Escape") { addingCrane = false; newCraneName = ""; } }}
            />
            <button class="btn" type="button" disabled={!newCraneName.trim()} on:click={addEmptyCrane} title="Start this crane with an empty planning">Start empty</button>
            <button class="btn" type="button" disabled={!newCraneName.trim()} on:click={addCraneFromP6} title="Import activities from a P6 XML export instead of starting empty">Import P6 XML…</button>
            <input type="file" accept=".xml,text/xml,application/xml" hidden bind:this={craneFileInput} on:change={onCraneFileChange} />
            <button class="btn" type="button" on:click={() => { addingCrane = false; newCraneName = ""; }}>Cancel</button>
          {:else}
            <button class="btn" type="button" on:click={() => addingCrane = true}>+ Add crane</button>
          {/if}
        {/if}
      </div>
    {/if}

    <div class="theme-switch" role="group" aria-label="Theme">
      {#each THEME_OPTIONS as opt (opt.id)}
        <button class="theme-btn" class:active={$theme === opt.id} type="button" on:click={() => theme.set(opt.id)}>
          {opt.label}
        </button>
      {/each}
    </div>

    {#if AUTH_REQUIRED && $auth}
      <div class="userbar">
        <button class="userbar-account" type="button" on:click={openProfileModal} title="My account">
          {#if myAvatar}
            <img class="userbar-avatar" src={myAvatar} alt="" />
          {/if}
          <span>{$auth.record.name || $auth.record.email} <span class="role-tag">{$role}</span></span>
        </button>
        <button class="btn" type="button" on:click={doSignOut}>Sign out</button>
      </div>
    {/if}
  </div>

  <div id="pbStatus" class="pb-status">{$pbStatus}{#if $currentPlanning} — {$currentPlanning.name}{/if}</div>
</header>
